/* eslint-disable jsx-a11y/alt-text */
import slugify from 'slugify';
import styled from 'styled-components';

import Button from '../../components/Button';
import { theme } from '../../components/GlobalStyles';
import { Box, Col, Contain, Row } from '../../components/Grid';
import Image from '../../components/Image';
import SeoHead from '../../components/SeoHead';
import exchangeBanner from '../../public/static/img/pages/exchange/banner.jpg';
import featureConvert from '../../public/static/img/pages/exchange/feature-convert.jpg';
import featurePoint from '../../public/static/img/pages/exchange/feature-points.jpg';
import { rmsGet } from '../../utils/fetch';
import lineBreaker from '../../utils/lineBreaker';
import withErrorHandling from '../../utils/withErrorHandling';
import ErrorPage from '../_error';

const StyledPartnerCard = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  .title {
    color: #fff;
  }
  .hero {
    padding-bottom: 52.356021%;
    position: relative;
  }
  .banner-holder {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
  }
  .banner {
    objec-fit: cover;
    width: 100%;
    height: 100%;
    display: block;
  }
  .logo {
    border-radius: 100%;
    width: 4.375rem;
    height: 4.375rem;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate3d(-50%, 50%, 0);
  }
  .content {
    height: 100%;
    padding: 2rem 1rem 1.5rem;
    color: #fff;
    background: ${theme.colors.purple};
    display: flex;
    flex-direction: column;
  }
  .subtitle {
    margin-top: 0;
    text-transform: uppercase;
    font-size: 1rem;
  }
  .rate-text {
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }
  .cta-button {
    margin-top: auto;
  }
`;

const PartnerCard = ({ id, title, text, logo, banner }) => (
  <StyledPartnerCard>
    <div className="hero" data-testid={`partner-card-${id}`}>
      <figure className="banner-holder">
        <img alt="" className="banner" src={banner} />
      </figure>
      <img alt="title" className="logo" src={logo} />
    </div>
    <div className="content">
      <h3 className="title">{title}</h3>
      <h4 className="subtitle">Conversion rate</h4>
      <div className="rate-text" dangerouslySetInnerHTML={{ __html: lineBreaker(text) }} />
      <Button
        href="/exchange/[permalink]"
        as={`/exchange/${id}-${slugify(title, { lower: true })}`}
        secondary
        white
        className="cta-button"
      >
        View more
      </Button>
    </div>
  </StyledPartnerCard>
);

const Exchange = ({ response, error }) => {
  if (error) {
    return <ErrorPage error={error} />;
  }
  return (
    <div className="text-center">
      <SeoHead
        title="Convert Linkpoints to Preferred Loyalty Points | Link Rewards"
        description="Plus! is now Link. Convert Linkpoints to your preferred loyalty points or miles with KrisFlyer, Park Hotel, AirAsia and more partners you love."
      />
      <Contain>
        <Image
          src={exchangeBanner}
          alt="Linkpoints exchange. Convert Linkpoints to your preferred loyalty points or miles."
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw,33vw"
        />

        <h1>Linkpoints exchange</h1>
        <h2>Convert Linkpoints to your preferred loyalty points or miles</h2>

        <Row flexDirection={['column', 'row']}>
          <Col flex="1">
            <Image
              alt="feature-point"
              src={featurePoint}
              width={274}
              height={185}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw,33vw"
            />
            <p>Don’t let your expiring unused points/miles go to waste.</p>
          </Col>
          <Col flex="1">
            <Image
              alt="feature-convert"
              src={featureConvert}
              width={274}
              height={185}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw,33vw"
            />
            <p>
              Convert your unused points/miles to your preferred programme and get closer to
              redeeming your favourite rewards.
            </p>
          </Col>
        </Row>
      </Contain>
      <Box
        css={`
          background-color: #f9f9f9;
        `}
        py="lg"
        mb="xl"
      >
        <Contain>
          <h2>Convert in 3 easy steps</h2>
          <Row flexDirection={['column', 'row']}>
            <Col flex="1">
              <img src="/static/img/pages/exchange/step-people.svg" width="84" />
              <p>Select a partner</p>
            </Col>
            <Col flex="1">
              <img src="/static/img/pages/exchange/step-convert.svg" width="84" />
              <p>Log in and connect with your selected partner</p>
            </Col>
            <Col flex="1">
              <img src="/static/img/pages/exchange/step-reward.svg" width="84" />
              <p>Complete the conversion to your preferred loyalty points or miles</p>
            </Col>
          </Row>
        </Contain>
      </Box>
      <Contain>
        <h2>Start converting your points with these partners</h2>
        <Row flexWrap={['nowrap', 'wrap']} flexDirection={['column', 'row']}>
          {response.exchanges
            ? response.exchanges.map((exchange) => (
                <Col key={exchange.id} flex="0 0 25%" maxWidth={['none', '25%']} mb="xl">
                  <PartnerCard
                    id={exchange.id}
                    title={exchange.merchantName}
                    logo={exchange.merchantLogoUrl}
                    banner={exchange.bigUrl}
                    text={exchange.shortConversionRateText}
                  />
                </Col>
              ))
            : null}
        </Row>
      </Contain>
    </div>
  );
};

export async function getData() {
  const data = await rmsGet('/exchanges');
  if (data.code !== '0000') {
    throw Error(data.code);
  }
  return {
    props: {
      response: data.response,
    },
  };
}

export const getServerSideProps = withErrorHandling(getData);

export default Exchange;
